import { Link } from 'react-router-dom';
import GuideLayout from '../../layouts/GuideLayout';
import type { TocItem } from '../../layouts/GuideLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { generateBreadcrumbList } from '../../utils/schemaHelpers';

const tocItems: TocItem[] = [
    { id: 'overview', label: 'What This Guide Collection Is', level: 1 },
    { id: 'learning-path', label: 'Learning Path', level: 1 },
    { id: 'pricing-traps', label: 'Pricing Traps & Hidden Costs', level: 1 },
    { id: 'deep-dives', label: 'Service Deep-Dives', level: 1 },
    { id: 'operational', label: 'Operational Guides', level: 1 },
    { id: 'choosing-a-guide', label: 'How to Choose a Guide', level: 1 },
    { id: 'beyond-guides', label: 'Beyond These Guides', level: 1 },
    { id: 'feedback', label: 'Feedback & Updates', level: 1 },
];

const bottomCta = (
    <AffiliateCTA
        programme="Pluralsight"
        courseName="AZ-104 Microsoft Azure Administrator"
        url="https://www.pluralsight.com/paths/az-104-microsoft-azure-fundamentals"
        price="From £29/month"
        badgeText="Covers all topics in these guides"
    />
);

export default function GuidesHubPage() {
    return (
        <>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'CollectionPage',
                        headline: 'Azure Pricing Guides | Authority Hub for UK Engineers',
                        description:
                            'Complete guide collection covering Azure pricing traps, service deep-dives, invoice reconciliation, and operational decision-making. UK South GBP pricing from the Azure Retail Prices API.',
                        author: { '@type': 'Organization', name: 'AzureCalc.uk' },
                        publisher: {
                            '@type': 'Organization',
                            name: 'AzureCalc.uk',
                            url: 'https://www.azure-calc.co.uk',
                        },
                        dateModified: '2026-04-12',
                        url: 'https://www.azure-calc.co.uk/guides',
                    }),
                }}
            />
            <script type="application/ld+json">
                {JSON.stringify(generateBreadcrumbList([
                    { name: 'Home', url: '/', position: 1 },
                    { name: 'Azure Pricing Guides', url: '/guides', position: 2 },
                ]))}
            </script>
            <GuideLayout
                title="Azure Pricing Guides — Authority Hub for UK Engineers"
                description="Navigate Azure pricing with confidence. Seven comprehensive guides covering billing traps, service deep-dives, invoice verification, and operational decision-making — all in UK South GBP."
                lastUpdated="April 2026"
                canonical="https://www.azure-calc.co.uk/guides"
                metaTitle="Azure Pricing Guides Hub | Authority Resource | AzureCalc.uk"
                metaDescription="Complete guide collection: billing traps, service deep-dives, invoice reconciliation, networking costs, egress explained, and more. UK South GBP pricing."
                tocItems={tocItems}
                relatedCalculators={[
                    { service: 'blob-storage', path: '/blob-storage', label: 'Blob Storage Calculator' },
                    { service: 'log-analytics', path: '/log-analytics', label: 'Log Analytics Calculator' },
                    { service: 'networking', path: '/networking', label: 'Networking Calculator' },
                ]}
                bottomCta={bottomCta}
            >
                <p className="text-[15px] italic mb-8" style={{ color: 'var(--text-tertiary)' }}>
                    Seven guides built to solve the gaps Azure's official documentation leaves. Each guide digs
                    into real cost patterns, trade-offs, and decision frameworks — not just meter lists.
                </p>

                <p className="text-[12px] italic mb-6" style={{ color: 'var(--text-tertiary)' }}>
                    All pricing data sourced from the Microsoft Azure Retail Prices API, updated nightly. UK South, GBP.
                </p>

                {/* ── Section: Overview ── */}
                <section id="overview">
                    <h2>What This Guide Collection Is</h2>
                    <p>
                        These guides exist because Azure pricing documentation is oriented toward completeness, not clarity.
                        Microsoft publishes thousands of pricing pages and meters — the official machinery is rigorous and
                        authoritative — but it leaves a gap: <strong>engineers evaluating a real architecture need guidance
                            that connects meters, explains trade-offs, and surfaces the traps</strong>.
                    </p>
                    <p>
                        Each guide in this collection tackles one complex pricing decision or one service area in depth of a way that:
                    </p>
                    <ul>
                        <li>Shows the formula or decision tree (not just the answer)</li>
                        <li>Uses real numbers from actual invoices / working systems</li>
                        <li>Explains the "why" behind awkward pricing choices</li>
                        <li>Links to the official Azure docs as the source of truth</li>
                        <li>Provides a worked example you can adapt to your own architecture</li>
                    </ul>
                    <p>
                        They are not official Microsoft documentation. They are not product support. They are the notes
                        of an independent Azure engineer who has been surprised by invoice bills, worked through cost
                        reconciliation, and built cost calculators. Every number is sourced from the Azure Retail Prices API
                        and verified against real invoice line items.
                    </p>
                </section>

                {/* ── Section: Learning Path ── */}
                <section id="learning-path">
                    <h2>Suggested Learning Path</h2>
                    <p>
                        New to Azure pricing? Start here and work through in order:
                    </p>

                    <div className="my-6 space-y-4">
                        {/* Step 1: Billing Traps */}
                        <div className="border-l-4 pl-4 py-2" style={{ borderColor: '#e8b000' }}>
                            <h3 className="font-bold mb-2 flex items-center gap-2">
                                <span className="material-symbols-outlined text-[#e8b000]">warning</span>
                                <span>Step 1: Learn the Traps</span>
                            </h3>
                            <p className="text-[14px]" style={{ color: 'var(--text-tertiary)' }}>
                                Start with{' '}
                                <Link to="/guides/azure-billing-traps-uk" className="text-[#0078d4] hover:underline">
                                    Azure Billing Traps
                                </Link>
                                . It covers seven real-world surprises: Archive rehydration minimums, Log Analytics 2TB restore
                                floors, NAT Gateway always-on charges, early deletion penalties, and more. These are not edge cases
                                — team production workloads hit these regularly.
                            </p>
                            <p className="text-[13px] mt-2 italic" style={{ color: 'var(--text-tertiary)' }}>
                                ~2,000 words | 15–20 min read
                            </p>
                        </div>

                        {/* Step 2: Deep Dives */}
                        <div className="border-l-4 pl-4 py-2" style={{ borderColor: '#0078d4' }}>
                            <h3 className="font-bold mb-2 flex items-center gap-2">
                                <span className="material-symbols-outlined text-[#0078d4]">description</span>
                                <span>Step 2: Choose Your Deep-Dive</span>
                            </h3>
                            <p className="text-[14px]" style={{ color: 'var(--text-tertiary)' }}>
                                Based on your current focus, pick one or more service-specific guides:
                            </p>
                            <ul className="text-[14px] mt-3 space-y-2 list-disc pl-5" style={{ color: 'var(--text-tertiary)' }}>
                                <li>
                                    <Link to="/guides/log-analytics-search-job-vs-restore" className="text-[#0078d4] hover:underline">
                                        Log Analytics: Search Job vs Restore
                                    </Link>
                                    {' — if you use Log Analytics + Sentinel (most common)'}
                                </li>
                                <li>
                                    <Link to="/guides/azure-networking-costs-uk" className="text-[#0078d4] hover:underline">
                                        Azure Networking Hidden Costs
                                    </Link>
                                    {' — if you design hybrid, hub-spoke, or multi-region architectures'}
                                </li>
                                <li>
                                    <Link to="/guides/azure-egress-bandwidth-costs-explained" className="text-[#0078d4] hover:underline">
                                        Azure Egress Costs Explained
                                    </Link>
                                    {" — if you're estimating data transfer or inter-region replication"}
                                </li>
                            </ul>
                            <p className="text-[13px] mt-3 italic" style={{ color: 'var(--text-tertiary)' }}>
                                ~1,500–2,000 words each | 20–30 min read each
                            </p>
                        </div>

                        {/* Step 3: Invoice Reconciliation */}
                        <div className="border-l-4 pl-4 py-2" style={{ borderColor: '#107c10' }}>
                            <h3 className="font-bold mb-2 flex items-center gap-2">
                                <span className="material-symbols-outlined text-[#107c10]">check_circle</span>
                                <span>Step 3: Verify Against Your Invoice</span>
                            </h3>
                            <p className="text-[14px]" style={{ color: 'var(--text-tertiary)' }}>
                                Once you've estimated a cost, use{' '}
                                <Link to="/guides/invoice-reconciliation" className="text-[#0078d4] hover:underline">
                                    Invoice Reconciliation
                                </Link>
                                {' '}to verify your estimate matches real invoices. This guide provides a 5-step method, a
                                real case study, and a DIY reconciliation template so you can understand why estimates diverge from
                                actuals.
                            </p>
                            <p className="text-[13px] mt-2 italic" style={{ color: 'var(--text-tertiary)' }}>
                                ~2,000 words | 25–30 min read
                            </p>
                        </div>
                    </div>
                </section>

                {/* ── Section: Pricing Traps ── */}
                <section id="pricing-traps">
                    <h2>Pricing Traps & Hidden Costs</h2>
                    <p>
                        These guides focus on the surprises, not the happy path.
                    </p>

                    <div
                        className="rounded-lg p-5 my-6"
                        style={{
                            backgroundColor: 'var(--surface-container-low)',
                            borderLeft: '4px solid #e8b000',
                        }}
                    >
                        <h3 className="text-[16px] font-bold mb-3 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[#e8b000]">warning</span>
                            Azure Billing Traps UK 2026
                        </h3>
                        <p className="text-[14px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
                            <strong>The headline dangers:</strong> Archive rehydration minimums, Log Analytics 2TB restore floors,
                            NAT Gateway always-on charges, Blob early deletion penalties, basic logs query cost traps, hub-spoke
                            double NAT charges, inter-region bandwidth.
                        </p>
                        <Link
                            to="/guides/azure-billing-traps-uk"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded text-[14px] font-medium transition-all"
                            style={{
                                backgroundColor: '#0078d4',
                                color: 'white',
                            }}
                        >
                            Read the Traps Guide →
                        </Link>
                    </div>

                    <p>
                        Why does a guide dedicated to traps matter? Because they're not theoretical. These are mistakes
                        I and other engineers have made on production systems. Each trap has a cost impact and a mitigation
                        strategy documented.
                    </p>
                </section>

                {/* ── Section: Deep-Dives ── */}
                <section id="deep-dives">
                    <h2>Service Deep-Dives</h2>
                    <p>
                        These guides zoom into specific Azure services, explain the cost model, identify the non-obvious drivers,
                        and provide decision frameworks for common scenarios.
                    </p>

                    <div className="my-6 grid gap-4">
                        {/* Log Analytics */}
                        <div className="border rounded-lg p-4" style={{ borderColor: 'var(--outline-variant)' }}>
                            <h3 className="text-[15px] font-bold mb-2">
                                <Link to="/guides/log-analytics-search-job-vs-restore" className="text-[#0078d4] hover:underline">
                                    Log Analytics: Search Job vs Restore
                                </Link>
                            </h3>
                            <p className="text-[13px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
                                Log Analytics charges differently for current data (Search Jobs at ~£0.0046/GB scanned) vs archived
                                data (Restore at 2TB minimum = ~£1.55/day minimum). This guide explains both, shows when to use each,
                                and provides a cost comparison calculator.
                            </p>
                            <div className="bg-[#107c10]/10 border border-[#107c10]/40 p-3 rounded-sm" style={{ color: 'var(--text-tertiary)' }}>
                                <p className="text-[12px] font-bold text-[#107c10] mb-1">Why this matters:</p>
                                <p className="text-[13px]">Restoring 5 GB of archived data costs the same as 2TB. Most teams don't know this.</p>
                            </div>
                        </div>

                        {/* Networking */}
                        <div className="border rounded-lg p-4" style={{ borderColor: 'var(--outline-variant)' }}>
                            <h3 className="text-[15px] font-bold mb-2">
                                <Link to="/guides/azure-networking-costs-uk" className="text-[#0078d4] hover:underline">
                                    Azure Networking Hidden Costs
                                </Link>
                            </h3>
                            <p className="text-[13px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
                                NAT Gateway ~£33/month (always-on, no pause state). Public IP Standard SKU ~£2.92/month. VNet peering
                                ~£0.015/GB per direction. DDoS Protection Standard ~£3,018/month. This guide breaks down each and
                                explains hub-spoke double-charge patterns.
                            </p>
                            <div className="bg-[#0078d4]/10 border border-[#0078d4]/40 p-3 rounded-sm" style={{ color: 'var(--text-tertiary)' }}>
                                <p className="text-[12px] font-bold text-[#0078d4] mb-1">Key insight:</p>
                                <p className="text-[13px]">NAT Gateway charges even when idle. Unlike VMs, you can't deallocate it — only delete.</p>
                            </div>
                        </div>

                        {/* Egress */}
                        <div className="border rounded-lg p-4" style={{ borderColor: 'var(--outline-variant)' }}>
                            <h3 className="text-[15px] font-bold mb-2">
                                <Link to="/guides/azure-egress-bandwidth-costs-explained" className="text-[#0078d4] hover:underline">
                                    Azure Egress Costs Explained
                                </Link>
                            </h3>
                            <p className="text-[13px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
                                Outbound internet transfer is tiered (free to 100 GB, then ~£0.070/GB for 100–10,000 GB). Inter-region
                                within Europe costs less than inter-region globally. Ingress is free. This guide explains the tier
                                mechanics and architecture trade-offs.
                            </p>
                            <div className="bg-[#e8b000]/10 border border-[#e8b000]/40 p-3 rounded-sm" style={{ color: 'var(--text-tertiary)' }}>
                                <p className="text-[12px] font-bold text-[#e8b000] mb-1">The math:</p>
                                <p className="text-[13px]">5 TB/month to the internet + 10 TB/month inter-region from UK South to North Europe costs ~£550/month.</p>
                            </div>
                        </div>

                        {/* Bonus: Data Factory, VDI, and More */}
                        <div className="border rounded-lg p-4" style={{ borderColor: 'var(--outline-variant)' }}>
                            <h3 className="text-[15px] font-bold mb-2">
                                <Link to="/guides/azure-data-factory-pricing-uk" className="text-[#0078d4] hover:underline">
                                    Azure Data Factory Pricing UK
                                </Link>
                            </h3>
                            <p className="text-[13px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
                                DIU-hours for Copy activities, vCore-hours for Data Flow orchestration, self-hosted integration
                                runtime licensing. Data Factory costs can balloon unexpectedly if not tuned properly.
                            </p>
                        </div>

                        <div className="border rounded-lg p-4" style={{ borderColor: 'var(--outline-variant)' }}>
                            <h3 className="text-[15px] font-bold mb-2">
                                <Link to="/guides/azure-backup-pricing-uk" className="text-[#0078d4] hover:underline">
                                    Azure Backup Pricing UK
                                </Link>
                            </h3>
                            <p className="text-[13px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
                                The per-instance fee is small — vault storage is the real cost driver. Why storage beats the instance fee, LRS vs GRS tradeoffs, and the retention policy multiplier effect.
                            </p>
                        </div>
                    </div>
                </section>

                {/* ── Section: Operational ── */}
                <section id="operational">
                    <h2>Operational Guides</h2>
                    <p>
                        These guides help you verify estimates and cross-check Azure costs against your actual bills and architecture.
                    </p>

                    <div className="my-6 space-y-4">
                        <div className="border-l-4 pl-4" style={{ borderColor: '#107c10' }}>
                            <h3 className="text-[15px] font-bold mb-2">
                                <Link to="/guides/invoice-reconciliation" className="text-[#0078d4] hover:underline">
                                    How to Verify Azure Estimates Against Real Invoices
                                </Link>
                            </h3>
                            <p className="text-[14px]" style={{ color: 'var(--text-tertiary)' }}>
                                The ultimate proof-of-work: showing that AzureCalc.uk estimates match real invoices. This guide provides
                                a 5-step reconciliation method with a real case study (Q1 2026 reconciliation with variance explanations)
                                and a DIY template you can adapt to verify your own estimates.
                            </p>
                            <p className="text-[13px] mt-2 italic" style={{ color: 'var(--text-tertiary)' }}>
                                Why this matters: Estimates often diverge from actuals due to Enterprise Agreement discounts, Reserved
                                Instances, committed use discounts, or optimization you didn't account for. This guide teaches you how to
                                find the gap and close it.
                            </p>
                        </div>
                    </div>
                </section>

                {/* ── Section: Choosing a Guide ── */}
                <section id="choosing-a-guide">
                    <h2>How to Choose a Guide</h2>
                    <p>
                        Not sure where to start? Use this decision tree:
                    </p>

                    <table className="w-full text-[14px] my-6 border-collapse">
                        <thead>
                            <tr style={{ backgroundColor: 'var(--surface-container-high)' }}>
                                <th className="text-left p-3 border" style={{ borderColor: 'var(--outline-variant)' }}>
                                    <strong>You are...</strong>
                                </th>
                                <th className="text-left p-3 border" style={{ borderColor: 'var(--outline-variant)' }}>
                                    <strong>Start with...</strong>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td
                                    className="p-3 border"
                                    style={{
                                        backgroundColor: 'var(--surface-container-low)',
                                        borderColor: 'var(--outline-variant)',
                                    }}
                                >
                                    New to Azure cost estimation
                                </td>
                                <td className="p-3 border" style={{ borderColor: 'var(--outline-variant)' }}>
                                    <Link to="/guides/azure-billing-traps-uk" className="text-[#0078d4] hover:underline">
                                        Billing Traps
                                    </Link>{' '}
                                    (birds-eye view) then pick a service deep-dive
                                </td>
                            </tr>
                            <tr>
                                <td className="p-3 border" style={{ borderColor: 'var(--outline-variant)' }}>
                                    Using Log Analytics + Sentinel
                                </td>
                                <td className="p-3 border" style={{ borderColor: 'var(--outline-variant)' }}>
                                    <Link to="/guides/log-analytics-search-job-vs-restore" className="text-[#0078d4] hover:underline">
                                        Log Analytics: Search vs Restore
                                    </Link>
                                </td>
                            </tr>
                            <tr>
                                <td
                                    className="p-3 border"
                                    style={{
                                        backgroundColor: 'var(--surface-container-low)',
                                        borderColor: 'var(--outline-variant)',
                                    }}
                                >
                                    Designing hybrid, hub-spoke, multi-region
                                </td>
                                <td className="p-3 border" style={{ borderColor: 'var(--outline-variant)' }}>
                                    <Link to="/guides/azure-networking-costs-uk" className="text-[#0078d4] hover:underline">
                                        Networking Hidden Costs
                                    </Link>{' '}
                                    +{' '}
                                    <Link to="/guides/azure-egress-bandwidth-costs-explained" className="text-[#0078d4] hover:underline">
                                        Egress Costs
                                    </Link>
                                </td>
                            </tr>
                            <tr>
                                <td className="p-3 border" style={{ borderColor: 'var(--outline-variant)' }}>
                                    Estimating archive or long-term storage
                                </td>
                                <td className="p-3 border" style={{ borderColor: 'var(--outline-variant)' }}>
                                    <Link to="/guides/azure-billing-traps-uk" className="text-[#0078d4] hover:underline">
                                        Billing Traps
                                    </Link>{' '}
                                    (Archive minimum section)
                                </td>
                            </tr>
                            <tr>
                                <td
                                    className="p-3 border"
                                    style={{
                                        backgroundColor: 'var(--surface-container-low)',
                                        borderColor: 'var(--outline-variant)',
                                    }}
                                >
                                    Reconciling estimates vs actual bills
                                </td>
                                <td className="p-3 border" style={{ borderColor: 'var(--outline-variant)' }}>
                                    <Link to="/guides/invoice-reconciliation" className="text-[#0078d4] hover:underline">
                                        Invoice Reconciliation
                                    </Link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </section>

                {/* ── Section: Beyond Guides ── */}
                <section id="beyond-guides">
                    <h2>Beyond These Guides</h2>
                    <p>
                        These guides cover the top cost drivers and traps, but Azure has dozens of services. For services not
                        covered here:
                    </p>

                    <ol className="list-decimal pl-5 space-y-2 my-4">
                        <li>
                            Use the{' '}
                            <a
                                href="https://azure.microsoft.com/pricing/calculator"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#0078d4] hover:underline"
                            >
                                official Azure Pricing Calculator
                            </a>{' '}
                            as the source of truth for pricing
                        </li>
                        <li>
                            Check the specific service's pricing page on{' '}
                            <a
                                href="https://learn.microsoft.com/en-us/azure/pricing"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#0078d4] hover:underline"
                            >
                                Microsoft Learn
                            </a>
                        </li>
                        <li>
                            If you find yourself reconcilingdifferences consistently, reach out at{' '}
                            <a href="mailto:gravitycontextdev@gmail.com" className="text-[#0078d4] hover:underline">
                                gravitycontextdev@gmail.com
                            </a>
                            {' — '}
                            I may expand the guide collection based on feedback
                        </li>
                    </ol>

                    <p>
                        All prices on AzureCalc.uk are sourced from the{' '}
                        <Link to="/methodology" className="text-[#0078d4] hover:underline">
                            Microsoft Azure Retail Prices API
                        </Link>
                        , updated nightly, and filtered to UK South and GBP. For other regions and price comparisons, use the
                        official calculator.
                    </p>
                </section>

                {/* ── Section: Feedback ── */}
                <section id="feedback">
                    <h2>Feedback, Errors & Updates</h2>
                    <p>
                        Found an error? Have a pricing guide topic you'd like covered? Think a guide is missing a section?
                    </p>

                    <p>
                        Email{' '}
                        <a href="mailto:gravitycontextdev@gmail.com" className="text-[#0078d4] hover:underline">
                            gravitycontextdev@gmail.com
                        </a>
                        . I read every email and act on substantive feedback. The guides are updated as Microsoft's pricing changes,
                        as I learn new cost patterns, and as the community surfaces overlooked edge cases.
                    </p>

                    <p className="mt-4 italic" style={{ color: 'var(--text-tertiary)' }}>
                        Last guide update: April 2026. Price data refreshes nightly at 02:00 UTC.
                    </p>
                </section>
            </GuideLayout>
        </>
    );
}
